import { useState } from 'react';
import { useAltHotkeys } from '../../../hooks/useAltHotkeys';
import { useModalDrag } from '../../../hooks/useModalDrag';
import ModalHeader from '../../../components/ui/ModalHeader';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { PencilSquareIcon } from '@heroicons/react/24/outline';
import { ScheduleStatus } from '../../../types/scheduling';

interface LCBulkEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (updates: LCBulkEditUpdates) => void;
  selectedCount: number;
}

export interface LCBulkEditUpdates {
  schedule_status?: ScheduleStatus;
  scheduling_comment?: string;
}

const STATUS_OPTIONS: { value: ScheduleStatus; label: string }[] = [
  { value: 'PENDING', label: 'Pending LC' },
  { value: 'PENDING_APPT', label: 'Pending Appt' },
  { value: 'HOLD', label: 'Hold' },
  { value: 'PENDING_CUSTOMER', label: 'Pending Customer' },
  { value: 'HOT_ORDER', label: 'Hot Order' },
  { value: 'RESCHEDULED', label: 'Rescheduled' },
];

export default function LCBulkEditModal({
  isOpen,
  onClose,
  onConfirm,
  selectedCount,
}: LCBulkEditModalProps) {
  const [status, setStatus] = useState<ScheduleStatus | ''>('');
  const [comment, setComment] = useState('');

  const handleConfirm = () => {
    const updates: LCBulkEditUpdates = {};
    if (status) updates.schedule_status = status;
    if (comment.trim()) updates.scheduling_comment = comment.trim();
    onConfirm(updates);
    handleClose();
  };

  const handleClose = () => {
    setStatus('');
    setComment('');
    onClose();
  };

  const hasUpdates = !!status || !!comment.trim();

  // Alt+C closes. Apply gets no chord — it writes status/comment to every
  // selected leg at once.
  useAltHotkeys({ C: onClose }, undefined, { layer: isOpen });

  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(isOpen);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        aria-labelledby="lc-bulk-edit-modal-title"
        onClose={handleClose}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-scrim bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Panel
                ref={panelRef}
                style={panelStyle}
                className="w-full max-w-md overflow-hidden rounded-xl bg-panel shadow-xl"
              >
                <ModalHeader
                  title="Bulk Edit"
                  titleId="lc-bulk-edit-modal-title"
                  onClose={handleClose}
                  dragHandleProps={dragHandleProps}
                />
                <div className="p-6">
                <div className="mb-6">
                  <p className="text-gray-600 dark:text-gray-300 mb-4">
                    Update {selectedCount} selected PRO{selectedCount !== 1 ? 's' : ''}.
                  </p>

                  <div className="space-y-4">
                    {/* Status */}
                    <div>
                      <label
                        htmlFor="bulk-status"
                        className="block text-sm font-medium text-label mb-1"
                      >
                        Status
                      </label>
                      <select
                        id="bulk-status"
                        value={status}
                        onChange={(e) => setStatus(e.target.value as ScheduleStatus | '')}
                        className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-body px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">-- No Change --</option>
                        {STATUS_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Scheduling Comment */}
                    <div>
                      <label
                        htmlFor="bulk-comment"
                        className="block text-sm font-medium text-label mb-1"
                      >
                        Scheduling Comment
                      </label>
                      <textarea
                        id="bulk-comment"
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        rows={3}
                        placeholder="Enter comment to add..."
                        className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-body px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                      />
                      <p className="text-xs text-muted mt-1">
                        If a PRO already has a comment, this will be appended with a timestamp.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-3">
                  <button
                    onClick={handleClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirm}
                    disabled={!hasUpdates}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-blue-600"
                  >
                    <PencilSquareIcon className="w-4 h-4" />
                    Apply to {selectedCount} PRO{selectedCount !== 1 ? 's' : ''}
                  </button>
                </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
